import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

export const defaultAdaptiveTextGroup = "default";

type AdaptiveTextMember = {
  fontSize: number;
  group: string;
};

type AdaptiveTextScopeValue = {
  fontSizes: ReadonlyMap<string, number>;
  reportCandidate: (id: string, group: string, fontSize: number) => void;
  unregister: (id: string) => void;
};

const AdaptiveTextScopeContext = createContext<AdaptiveTextScopeValue | null>(
  null,
);

export const AdaptiveTextScope = ({ children }: { children: ReactNode }) => {
  const members = useRef(new Map<string, AdaptiveTextMember>());
  const [fontSizes, setFontSizes] = useState<ReadonlyMap<string, number>>(
    () => new Map(),
  );

  const recalculate = useCallback((groups: readonly string[]) => {
    setFontSizes((current) => {
      const next = new Map(current);
      let changed = false;

      for (const group of new Set(groups)) {
        const candidates = [...members.current.values()]
          .filter((member) => member.group === group)
          .map((member) => member.fontSize);
        const fontSize =
          candidates.length > 0 ? Math.min(...candidates) : undefined;

        if (fontSize === undefined) {
          changed = next.delete(group) || changed;
        } else if (next.get(group) !== fontSize) {
          next.set(group, fontSize);
          changed = true;
        }
      }

      return changed ? next : current;
    });
  }, []);

  const reportCandidate = useCallback(
    (id: string, group: string, fontSize: number) => {
      const previous = members.current.get(id);
      if (previous?.group === group && previous.fontSize === fontSize) return;

      members.current.set(id, { fontSize, group });
      recalculate(
        previous && previous.group !== group
          ? [previous.group, group]
          : [group],
      );
    },
    [recalculate],
  );

  const unregister = useCallback(
    (id: string) => {
      const previous = members.current.get(id);
      /* v8 ignore next -- effect cleanup is intentionally idempotent */
      if (!previous) return;

      members.current.delete(id);
      recalculate([previous.group]);
    },
    [recalculate],
  );

  const value = useMemo(
    () => ({ fontSizes, reportCandidate, unregister }),
    [fontSizes, reportCandidate, unregister],
  );

  return (
    <AdaptiveTextScopeContext.Provider value={value}>
      {children}
    </AdaptiveTextScopeContext.Provider>
  );
};

export const useAdaptiveTextScope = () => useContext(AdaptiveTextScopeContext);
